import { useEffect, useId, useRef } from 'react'
import { Icon } from '@iconify/react'
import { ACHIEVEMENTS } from '../game'
import type { AchievementProfile, GameStateV2 } from '../game'
import { decisionAsset } from '../ui/asset'
import { ClubCrest } from './ClubCrest'
import './AchievementDialog.css'

interface AchievementDialogProps {
  open: boolean
  state: GameStateV2 | null
  profile: AchievementProfile
  onClose: () => void
}

export function AchievementDialog({ open, profile, onClose }: AchievementDialogProps) {
  const titleId = useId()
  const dialogRef = useRef<HTMLElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!open) return
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const previousOverflow = document.body.style.overflow
    const previousRootOverflow = document.documentElement.style.overflow
    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'
    const frame = requestAnimationFrame(() => closeRef.current?.focus())
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); onCloseRef.current(); return }
      if (event.key !== 'Tab' || !dialogRef.current) return
      const focusable = [...dialogRef.current.querySelectorAll<HTMLElement>('button:not(:disabled)')]
      const first = focusable[0]
      const last = focusable.at(-1)
      if (!first || !last) return
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      cancelAnimationFrame(frame)
      document.body.style.overflow = previousOverflow
      document.documentElement.style.overflow = previousRootOverflow
      document.removeEventListener('keydown', onKeyDown)
      opener?.focus()
    }
  }, [open])

  if (!open) return null
  const unlocked = ACHIEVEMENTS.filter(({ id }) => profile.unlocked[id])
  const hidden = ACHIEVEMENTS.filter(({ id }) => !profile.unlocked[id])
  const unlockedCount = unlocked.length

  return (
    <div className="dialog-backdrop achievement-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose()
    }}>
      <section ref={dialogRef} className="achievement-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className="achievement-dialog__header">
          <span className="achievement-dialog__trophy"><Icon icon="mdi:trophy-outline" /></span>
          <div><p className="eyebrow">Palmarés permanente · {unlockedCount}/{ACHIEVEMENTS.length}</p><h2 id={titleId}>Logros de carrera</h2><p>Los retos permanecen ocultos hasta conseguirlos y se conservan al empezar una nueva carrera.</p></div>
          <button ref={closeRef} type="button" onClick={onClose} aria-label="Cerrar logros"><Icon icon="mdi:close" /></button>
        </header>
        <div className="achievement-dialog__content">
          {unlocked.length ? <section className="achievement-unlocked" aria-labelledby={`${titleId}-unlocked`}>
            <header className="achievement-section-title"><div><p>Archivo abierto</p><h3 id={`${titleId}-unlocked`}>Conseguidos</h3></div><strong>{unlocked.length}</strong></header>
            <ol className="achievement-list" aria-label="Logros conseguidos">
              {unlocked.map((achievement) => <li className="is-unlocked" key={achievement.id}>
              <article>
                <div className="achievement-list__art"><img src={decisionAsset(`destination-${achievement.art}`)} alt="" loading="lazy" />{achievement.club && <span className="achievement-list__club"><ClubCrest name={achievement.club} size="large" /></span>}<span><Icon icon="mdi:check-bold" /></span></div>
                <div className="achievement-list__body">
                  <div className="achievement-list__copy">
                    <p>{achievement.club ? `Reto de ${achievement.club}` : achievement.unlockTiming === 'career-end' ? 'Al terminar la carrera' : 'Durante la carrera'}</p>
                    <h3>{achievement.name}</h3>
                    <p>{achievement.description}</p>
                  </div>
                  <div className="achievement-list__progress"><span><strong>Conseguido</strong><small>100%</small></span><i><b style={{ width: '100%' }} /></i></div>
                  <footer><Icon icon="mdi:lock-open-variant-outline" /><span><strong>Desbloqueo</strong><small>Recompensa por determinar</small></span></footer>
                </div>
              </article>
              </li>)}
            </ol>
          </section> : <p className="achievement-empty"><Icon icon="mdi:trophy-outline" /><span><strong>Todavía no has desbloqueado ningún logro.</strong><small>Los secretos aparecerán aquí con todos sus detalles cuando los consigas.</small></span></p>}
          {hidden.length > 0 && <section className="achievement-secrets" aria-labelledby={`${titleId}-secrets`}>
            <header className="achievement-section-title"><div><p>Por descubrir</p><h3 id={`${titleId}-secrets`}>Retos secretos</h3></div><strong>{hidden.length}</strong></header>
            <ol className="achievement-secret-grid" aria-label="Logros secretos">
              {hidden.map((achievement, index) => <li aria-label="Logro oculto" key={achievement.id}>
                <span aria-hidden="true"><Icon icon="mdi:lock-outline" /></span>
                <small>Secreto {String(index + 1).padStart(2, '0')}</small>
              </li>)}
            </ol>
          </section>}
        </div>
      </section>
    </div>
  )
}
